import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Bug, AlertTriangle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../../components/layout/MainLayout';

const BugReport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    stepsToReproduce: '',
    severity: 'Medium'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api('/support/bug-report', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.message || 'Failed to submit bug report');
      }
    } catch (err) {
      setError('Connection error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px',
    borderRadius: '8px', border: '1px solid #e2e8f0',
    fontSize: '14px', outline: 'none',
    background: '#ffffff', color: '#1e293b',
    transition: 'border-color 0.2s ease', boxSizing: 'border-box',
    marginBottom: '16px'
  };

  const labelStyle = { display: 'block', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '8px' };

  if (success) {
    return (
      <MainLayout title="Report a Bug">
        <div style={{ padding: '40px', maxWidth: '600px', margin: '40px auto', background: 'white', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <CheckCircle size={64} color="#10b981" style={{ margin: '0 auto 24px' }} />
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '16px' }}>Bug Reported Successfully</h2>
          <p style={{ color: '#64748b', marginBottom: '32px' }}>Thank you for helping us improve! Our engineering team will review this shortly.</p>
          <button onClick={() => setSuccess(false)} style={{ padding: '12px 24px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
            Report Another Bug
          </button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Report a Bug">
      <div style={{ padding: '32px', maxWidth: '800px', margin: '0 auto', fontFamily: "'Inter', sans-serif" }}>
        
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Bug size={24} color="#ef4444" />
          </div>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0' }}>Report a Bug</h1>
            <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>Found an issue? Let us know so we can fix it.</p>
          </div>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '12px 16px', borderRadius: '8px', marginBottom: '24px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={18} /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Reporter Name</label>
              <input type="text" value={user?.name || ''} disabled style={{ ...inputStyle, background: '#f8fafc', color: '#94a3b8' }} />
            </div>
            <div>
              <label style={labelStyle}>Reporter Email</label>
              <input type="text" value={user?.email || ''} disabled style={{ ...inputStyle, background: '#f8fafc', color: '#94a3b8' }} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Bug Title *</label>
            <input type="text" name="title" value={formData.title} onChange={handleChange} required placeholder="Brief summary of the issue" style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Description *</label>
            <textarea name="description" value={formData.description} onChange={handleChange} required placeholder="Detailed description of what happened..." style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }} />
          </div>

          <div>
            <label style={labelStyle}>Steps to Reproduce</label>
            <textarea name="stepsToReproduce" value={formData.stepsToReproduce} onChange={handleChange} placeholder="1. Go to page...&#10;2. Click on...&#10;3. Observe error..." style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }} />
          </div>

          <div>
            <label style={labelStyle}>Severity</label>
            <select name="severity" value={formData.severity} onChange={handleChange} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="Low">Low - Minor UI issue or inconvenience</option>
              <option value="Medium">Medium - Feature not working as expected but workaround exists</option>
              <option value="High">High - Important feature broken, no workaround</option>
              <option value="Critical">Critical - System crash, data loss, or security issue</option>
            </select>
          </div>

          <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={loading} style={{ padding: '12px 24px', background: loading ? '#93c5fd' : '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.2s' }}>
              {loading ? 'Submitting...' : <><Bug size={18} /> Submit Bug Report</>}
            </button>
          </div>
        </form>

      </div>
    </div>
    </MainLayout>
  );
};

export default BugReport;
